import React from 'react';
import { useWeb3 } from '../contexts/Web3Context';

const useDiagnostics = () => {
    const {
        wallet,
        isConnected,
        web3Logs
    } = useWeb3();

    // Derive address and chainId from wallet object (not directly exposed in context)
    const address = wallet?.accounts?.[0]?.address || null;
    const chainId = wallet?.chains?.[0]?.id ? parseInt(wallet.chains[0].id, 16) : null;

    // Device Detection
    const getDeviceInfo = () => {
        const ua = navigator.userAgent;

        // Mobile detection
        const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
        const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
        const isIOS = /iPhone|iPad|iPod/i.test(ua);
        const isAndroid = /Android/i.test(ua);

        // Browser detection
        const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
        const isChrome = /Chrome/i.test(ua) && !/Edge/i.test(ua);
        const isFirefox = /Firefox/i.test(ua);
        const isEdge = /Edge/i.test(ua);

        // OS detection
        let os = 'Unknown';
        if (isIOS) {
            const match = ua.match(/OS (\d+)_(\d+)/);
            os = match ? `iOS ${match[1]}.${match[2]}` : 'iOS';
        } else if (isAndroid) {
            const match = ua.match(/Android (\d+(\.\d+)?)/);
            os = match ? `Android ${match[1]}` : 'Android';
        } else if (ua.includes('Mac OS X')) {
            const match = ua.match(/Mac OS X (\d+_\d+)/);
            os = match ? `macOS ${match[1].replace('_', '.')}` : 'macOS';
        } else if (ua.includes('Windows')) {
            os = 'Windows';
        } else if (ua.includes('Linux')) {
            os = 'Linux';
        }

        // Browser version
        let browserVersion = 'Unknown';
        if (isSafari) {
            const match = ua.match(/Version\/(\d+\.\d+)/);
            browserVersion = match ? match[1] : 'Unknown';
        } else if (isChrome) {
            const match = ua.match(/Chrome\/(\d+\.\d+)/);
            browserVersion = match ? match[1] : 'Unknown';
        } else if (isFirefox) {
            const match = ua.match(/Firefox\/(\d+\.\d+)/);
            browserVersion = match ? match[1] : 'Unknown';
        }

        return {
            userAgent: ua,
            isMobile,
            isTablet,
            isDesktop: !isMobile && !isTablet,
            os,
            browser: isSafari ? 'Safari' : isChrome ? 'Chrome' : isFirefox ? 'Firefox' : isEdge ? 'Edge' : 'Unknown',
            browserVersion,
            platform: navigator.platform,
            language: navigator.language,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
        };
    };

    // WalletConnect Status
    const getWalletConnectStatus = () => {
        // Try to access the mobile provider from Web3Context
        // This is a best-effort attempt - actual implementation may vary
        return {
            isConnected,
            address: address || null,
            chainId: chainId || null,
            hasWallet: !!wallet,
        };
    };

    // Network Status
    const getNetworkStatus = () => {
        const expectedChainId = 56; // BSC Mainnet
        const isCorrectChain = chainId === expectedChainId;

        return {
            online: navigator.onLine,
            chainId: chainId || null,
            expectedChainId,
            isCorrectChain,
            chainName: chainId === 56 ? 'BSC Mainnet' : chainId === 97 ? 'BSC Testnet' : chainId ? `Chain ${chainId}` : 'Not Connected',
        };
    };

    return {
        deviceInfo: getDeviceInfo(),
        walletStatus: getWalletConnectStatus(),
        networkStatus: getNetworkStatus(),
        logs: web3Logs || [],
    };
};

export default useDiagnostics;
